export default {
  checkout: {
    title: "Choose a plan",
    subtitle: "Select a plan to start using Lewara.",
    locked_title: "Activate your subscription",
    locked_subtitle: "Pick a plan to unlock your Lewara workspace. It only takes a minute.",
    pay_title: "Confirm & pay",
    pay_subtitle: "Review your plan and enter your payment details.",
    change_plan: "Change plan",
    logout: "Log out",
    eyebrow: "Checkout",
    step_plan: "Choose plan",
    step_payment: "Payment",
    preparing: "Preparing your secure checkout …",
    secure_note: "Payments are securely processed by Stripe.",
    mock_card_label: "Card information",
    mock_note: "Test mode: no real payment is processed.",
  },
  // What a user without MANAGE_BILLING gets instead of the checkout. The copy talks about the permission
  // rather than the subscription status: without VIEW_BILLING the page cannot read that status at all.
  noAccess: {
    eyebrow: "Subscription",
    title: "You can't manage billing",
    subtitle: "Only authorised people can manage this workspace's subscription.",
    // Paywall: the workspace stays locked until somebody subscribes.
    paywall_contacts:
      "There is nothing for you to complete here. Contact one of these people to get the workspace unlocked.",
    paywall_generic:
      "There is nothing for you to complete here. Contact your administrator to get the workspace unlocked.",
    // Management: the workspace works, this particular change does not.
    manage_contacts: "You can't change the subscription. Contact one of these people if something needs adjusting.",
    manage_generic: "You can't change the subscription. Contact your administrator if something needs adjusting.",
  },
  plan: {
    basic: "Basic",
    pro: "Pro",
    enterprise: "Enterprise",
    recommended: "Most popular",
    includes: "What's included",
    basic_tagline: "Everything you need to get started.",
    pro_tagline: "For growing teams that need more.",
    enterprise_tagline: "Advanced needs and full control.",
  },
  featureSet: {
    core: "Manage customers, employees, machines & materials",
    project_management: "Project management",
    invoicing: "Invoicing",
    reporting: "Reporting",
    personnel_planning: "Personnel planning",
    time_tracking: "Time tracking",
    document_design: "PDF templates",
    day_submissions: "Day submissions",
    advanced_analytics: "Advanced analytics",
    api_access: "API access",
  },
  // Short "showcase" copy for the info (ⓘ) buttons: one entry per feature set, plan, plus seats/add-ons.
  showcase: {
    core: {
      desc: "The everyday essentials every plan is built on: customers, projects and your team in one place.",
      p1: "Manage customers, employees and machines",
      p2: "A shared workspace for your whole team",
      p3: "Set up in minutes, no training required",
    },
    project_management: {
      desc: "Plan, track and deliver projects from the first quote to the final handover.",
      p1: "Structure work into projects and tasks",
      p2: "Track progress, costs and deadlines",
      p3: "Keep documents and notes with each project",
    },
    invoicing: {
      desc: "Turn logged work and materials into clean, professional invoices.",
      p1: "Generate invoices from hourly and material reports",
      p2: "Check as a draft, then bill",
      p3: "Export as PDF, ready to send",
    },
    reporting: {
      desc: "See where time and money go with ready-made operational reports.",
      p1: "[[Hwr.pl]] and [[cr.pl]]",
      p2: "Filter by project, employee or period",
      p3: "Export results for accounting",
    },
    personnel_planning: {
      desc: "Schedule the right people onto the right jobs and avoid clashes.",
      p1: "Visual drag-and-drop planning board",
      p2: "Spot conflicts and gaps at a glance",
      p3: "Share the plan with your crew",
    },
    time_tracking: {
      desc: "Define working-time models, record hours, and see what labour costs on each project.",
      p1: "Full-time, part-time and shift as separate models",
      p2: "Start, end and breaks, also by the foreman for the whole crew",
      p3: "Statutory breaks deducted automatically",
    },
    document_design: {
      desc: "[[cr.pl]], invoices and every other PDF document in your company's own design.",
      p1: "Your own letterhead with logo, colours, font and footer",
      p2: "Decide per document what it shows and in which order",
      p3: "Several templates, picked per project",
    },
    advanced_analytics: {
      desc: "Go beyond standard reports with deeper insight into your operation.",
      p1: "Trends across projects and periods",
      p2: "Profitability and utilisation metrics",
      p3: "Dashboards that update in real time",
    },
    api_access: {
      desc: "Connect Lewara to your other tools through a secure REST API.",
      p1: "Read and write your data programmatically",
      p2: "Automate exports and integrations",
      p3: "Scoped API keys you control",
    },
    basic: {
      desc: "Everything a small team needs to run day-to-day work in Lewara.",
      p1: "Core workspace for customers and projects",
      p2: "Ideal for getting started",
      p3: "Upgrade any time as you grow",
    },
    pro: {
      desc: "More power for growing teams: planning, invoicing and reporting included.",
      p1: "Everything in Basic, plus planning & invoicing",
      p2: "Built for busier, larger crews",
      p3: "Our most popular choice",
    },
    enterprise: {
      desc: "The full toolset with advanced analytics and API access for demanding operations.",
      p1: "Every feature Lewara offers",
      p2: "Advanced analytics and API access",
      p3: "Best value at scale",
    },
    seats: {
      desc: "A seat is for someone who signs in to Lewara, not for everyone on your team. You can keep far more employees in the system than you have seats; only the people who actually log in need one.",
      p1: "Seats are just for people who log in",
      p2: "Manage far more employees than seats; the rest simply don't sign in",
      p3: "Add or remove seats anytime; you're billed for the seats you keep",
    },
    addons: {
      desc: "Optional feature packs you can switch on for any plan. Pay only for what you need.",
      p1: "Add capabilities without changing plan",
      p2: "Turn them on or off any time",
      p3: "Some are already included in higher plans",
    },
  },
  addon: {
    label: "Add-on features",
  },
  seats: {
    label: "Seats",
    hint: "One seat is required per web user.",
    used: "Used",
    purchased: "Purchased",
    buy_more: "Buy more seats",
  },
  price: {
    summary_hint: "Exact pricing is shown at the payment step.",
    per_seat: "per seat",
    per_month: "per month",
    total: "Total",
  },
  payment: {
    continue: "Continue to payment",
    back: "Back",
    confirm: "Confirm payment",
    processing: "Processing payment …",
    success: "Payment successful",
    error: "The payment could not be processed.",
    pending: "Your payment is still processing. Please try again shortly.",
    declined: "Your card was declined, so the plan change wasn't applied. Update your payment method and try again.",
  },
  // Stripe Checkout return page (redirect-based / SCA payment methods only).
  return: {
    title: "Finishing up",
    subtitle: "Confirming your payment with Stripe.",
    processing: "Confirming your payment …",
    pending: "Your payment is still processing. This can take a moment. You don't need to pay again.",
  },
  grace: {
    banner: "Your payment failed. Please update your payment details. {days} day(s) left before lockout.",
    action: "Go to subscription",
  },
  // States of the subscription indicator (navigation drawer, subscription settings). Deliberately
  // short: the indicator sits in a 256px drawer. Explanations belong in the `_detail` lines.
  status: {
    active: "Active",
    // Cancelled but still running to the period end — "Cancelled" alone would read as "no access".
    canceling: "Ending",
    canceling_detail: "Ends on {date}",
    grace: "Payment due",
    grace_detail: "{days} day(s) of access left",
    // Subscription created, first payment not confirmed yet.
    pending: "Payment pending",
    // The period ran out after a cancellation — a different thing from a lockout over a failed payment.
    expired: "Ended",
    locked: "Locked",
    exempt: "Free",
    none: "No subscription",
    unknown: "Unknown",
  },
  plans: {
    eyebrow: "Pricing",
    title: "Plans & pricing",
    subtitle: "Compare plans and pick what fits your team. Adjust seats and add-ons any time.",
    choose: "Choose plan",
    change: "Change plan",
    get_started: "Get started",
    current: "Current",
    current_plan: "Current plan",
    login: "Log in",
    signup: "Sign up",
    back_to_app: "Back to app",
    home: "Go to Lewara home",
    interval_month: "/month",
    interval_year: "/year",
    cadence: "Billing cycle",
    cadence_month: "Monthly",
    cadence_year: "Yearly",
    save_percent: "Save {percent}%",
    per_month_billed_yearly: "≈ {price}/month, billed yearly",
    for_seats_one: "for {count} user",
    for_seats_other: "for {count} users",
    team_size: "Team size",
    per_user_short: "/user",
    custom: "Custom",
    most_popular: "Most popular",
    addon_optional: "Optional add-on",
    addon_included_in: "Included in {plans}",
    everything_in: "Everything in {plan}",
    addon_add: "Add",
    addon_added: "Added",
    addon_included: "Included",
    addon_in_plan: "Included in your plan",
    trust_secure: "Secure payments via Stripe",
    trust_cancel: "Cancel anytime",
    trust_support: "Friendly support",
  },
  summary: {
    title: "Order summary",
    total: "Total",
    base_plan: "Base plan",
    seats_line: "Seats · {count} × {price}",
    cadence_month: "Billed monthly",
    cadence_year: "Billed annually",
    discount_line: "Yearly discount",
    change: "Change",
    note: "Payment details are collected at checkout.",
  },
  invoices: {
    title: "Invoices",
    empty: "No invoices yet.",
    date: "Date",
    number: "Number",
    amount: "Amount",
    status: "Status",
    document: "Invoice",
    download: "Download",
    view: "View",
    status_paid: "Paid",
    status_open: "Open",
    status_void: "Void",
    status_draft: "Draft",
    status_uncollectible: "Uncollectible",
  },
  settings: {
    // Not "Billing": this tab is the SaaS subscription (plan, seats, payment method). Billing in
    // the customer sense is the hourly-wage wizard in the navigation (`nav.invoice`).
    title: "Subscription",
    // Deliberately no longer "Subscription" — that is the tab title directly above it.
    plan_section: "Plan",
    current_plan: "Current plan",
    addons: "Add-on features",
    seats: "Seats",
    period_end: "Current period ends on",
    billing_cycle: "Billing cycle",
    card: "Payment method",
    change_plan: "Change plan",
    update_card: "Update payment method",
    card_unavailable: "Updating the payment method is not available right now. Please try again later.",
    manage: "Manage subscription",
    // Shown in place of the change/cancel actions for a reader with VIEW_BILLING but not MANAGE_BILLING.
    manage_denied: "Only authorised people can change or cancel this subscription.",
    back: "Back to subscription",
    change_plan_subtitle: "Switch tier or adjust seats and add-ons. Changes take effect right away.",
    interval_switch_note:
      "Switching the billing cycle takes effect immediately. The difference for the remainder of your current period is prorated and settled with your next invoice.",
    update_card_subtitle: "Update the card charged for your subscription.",
    card_saved: "Payment method updated.",
    cancel: "Cancel subscription",
    cancel_confirm_title: "Cancel subscription?",
    cancel_confirm_text: "Your subscription stays active until the end of the current period.",
    exempt_notice: "Your account is exempt from billing.",
    cancels_at_period_end: "Will cancel at the end of the period.",
  },
  seatDialog: {
    title: "Add more seats",
    description: "You have reached your seat limit. Buy more seats to create this user.",
    current: "Currently {current} of {max} seats used.",
    confirm: "Buy seats",
  },
  // Add-on upsell shown when a feature-set the tenant has not purchased is opened (FeatureLockedView).
  locked: {
    eyebrow: "Add-on",
    title: "{feature} is not part of your plan",
    subtitle: "Add it to your subscription to start using it right away.",
    add: "Add to my plan",
    upgrade: "Upgrade my plan",
    back: "Back to dashboard",
    note: "You can review everything before you confirm.",
    // For a feature-set that is not on sale yet (neither an add-on nor part of a plan).
    unavailable_title: "Not available yet: {feature}",
    unavailable_text: "We are rolling this feature out gradually. It cannot be added to a plan yet.",
    unavailable_contact: "Would you like to use it already? Write to us:",
  },
}
